import { Injectable } from "@angular/core";
import { ToastController } from "@ionic/angular";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { take } from "rxjs/operators";
import { ApiService } from "src/app/services/api/api.service";
import { create_event, teaming_up_group } from "./model";
import moment from 'moment';
import 'moment-timezone';
import { PandaConnectBLL } from "../../panda-connect/panda-connect.bll";
import { FieldSchedulesBLL } from "../field-schedules.bll";
import { FieldScheduleReportBLL } from "../../field-schedule-report/field-schedule-report.bll";

@Injectable({
  providedIn: "root"
})
export class NewFieldEventBLL {

  private isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly isLoading$: Observable<boolean> = this.isLoading.asObservable();

  private schools: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly schools$: Observable<any> = this.schools.asObservable();

  private panda_clubs: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly panda_clubs$: Observable<any> = this.panda_clubs.asObservable();

  private is_panda_clubs_loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly is_panda_clubs_loading$: Observable<boolean> = this.is_panda_clubs_loading.asObservable();

  private ad_contacts: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly ad_contacts$: Observable<any> = this.ad_contacts.asObservable();

  private referee_contacts: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly referee_contacts$: Observable<any> = this.referee_contacts.asObservable();

  private blocked_referees: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  readonly blocked_referees$: Observable<any[]> = this.blocked_referees.asObservable();

  private field_facilities: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly field_facilities$: Observable<any> = this.field_facilities.asObservable();

  private bus_contacts: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly bus_contacts$: Observable<any> = this.bus_contacts.asObservable();

  private event_staff_contacts: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly event_staff_contacts$: Observable<any> = this.event_staff_contacts.asObservable();

  private isTeamsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly isTeamsLoading$: Observable<boolean> = this.isTeamsLoading.asObservable();

  private event_created: BehaviorSubject<string> = new BehaviorSubject<string>('');
  readonly event_created$: Observable<string> = this.event_created.asObservable();
  private leagues: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly leagues$: Observable<any> = this.leagues.asObservable();

  private event_updated: BehaviorSubject<string> = new BehaviorSubject<string>('');
  readonly event_updated$: Observable<string> = this.event_updated.asObservable();

  private event: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  readonly event$: Observable<any> = this.event.asObservable();

  private teams: BehaviorSubject<teaming_up_group[]> = new BehaviorSubject<teaming_up_group[]>([]);
  readonly teams$: Observable<teaming_up_group[]> = this.teams.asObservable();

  private opposing_teams: BehaviorSubject<teaming_up_group[]> = new BehaviorSubject<teaming_up_group[]>([]);
  readonly opposing_teams$: Observable<teaming_up_group[]> = this.opposing_teams.asObservable();

  private teaming_groups: BehaviorSubject<teaming_up_group[]> = new BehaviorSubject<teaming_up_group[]>([]);
  readonly teaming_groups$: Observable<teaming_up_group[]> = this.teaming_groups.asObservable();


  private show_addMenu: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly show_addMenu$: Observable<boolean> = this.show_addMenu.asObservable();

  private open_source: BehaviorSubject<string> = new BehaviorSubject<string>('');
  readonly open_source$: Observable<string> = this.open_source.asObservable();

  private group_activity: BehaviorSubject<any> = new BehaviorSubject<any>({});
  readonly group_activity$: Observable<any> = this.group_activity.asObservable();

  selectedFacility$: Subject<any> = new Subject<any>();
  selectedBuses$: Subject<any> = new Subject<any>();
  selectedAD$: Subject<any> = new Subject<any>();
  selectedReferee$: Subject<any> = new Subject<any>();
  selectedEventStaff$: Subject<any> = new Subject<any>();


  private practiceLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly practiceLoading$: Observable<boolean> = this.practiceLoading.asObservable();

  private practice_created: BehaviorSubject<string> = new BehaviorSubject<string>('');
  readonly practice_created$: Observable<string> = this.practice_created.asObservable();

  private practice_updated: BehaviorSubject<string> = new BehaviorSubject<string>('');
  readonly practice_updated$: Observable<string> = this.practice_updated.asObservable();

  constructor(
    private api: ApiService,
    private toast: ToastController,
    private connectBll: PandaConnectBLL,
    private fieldBll: FieldSchedulesBLL,
    private fieldScheduleReportBLL: FieldScheduleReportBLL

  ) { }

  toggle_add_menu(show: boolean, admin_id: string = null, school_id: string = null) {

    if (show) {
      this.connectBll.getSports();
      this.get_schools(admin_id, school_id);
      this.get_assigned_leagues(admin_id);
      this.reset_practice_loading();
    }

    this.show_addMenu.next(show);
  }

  set_open_source(source: string) {
    this.open_source.next(source);
  }

  reset_observables() {
    this.schools.next([]);
  }

  get_schools(admin_id: string = null, school_id: string = null) {
    this.isLoading.next(true);
    let url = `field-events`;

    if (admin_id) {
      url += `?admin_id=${admin_id}`;
    }

    if (!admin_id && school_id) {
      url += `?high_schools_id=${school_id}`;
    } else if (admin_id && school_id) {
      url += `&high_schools_id=${school_id}`;
    }

    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      this.schools.next(res.my_clubs);
      this.ad_contacts.next(res.ad_contacts);
      this.referee_contacts.next(res.referee_contacts);
      this.field_facilities.next(res.field_facilities);
      this.bus_contacts.next(res.bus_contacts);
      this.event_staff_contacts.next(res.event_staff_contacts);

    }, (err) => {
      this.isLoading.next(false);
    })

  }

  get_panda_clubs(keyword: string) {
    this.is_panda_clubs_loading.next(true);
    let url = `athlete?type=get_panda_clubs&search=${keyword}&page=1&limit=100`;

    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.is_panda_clubs_loading.next(false);
      this.panda_clubs.next(res.clubs || res.panda_clubs || res || []);
    }, (err) => {
      this.is_panda_clubs_loading.next(false);
    });
  }

  clear_panda_clubs() {
    this.panda_clubs.next([]);
  }

  public get_assigned_leagues(admin_id: string = null) {
    this.isLoading.next(true);

    let url = `league?type=get_leagues_assigned`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    this.api
      .get(url)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.leagues.next(
            response.assigned_leagues || response.leagues || [],
          );
          this.isLoading.next(false);
        },
        (err) => {
          this.isLoading.next(false);
          console.error('Error fetching leagues:', err);
        },
      );
  }

  get_facilities(id) {
    this.isLoading.next(true);
    let url = `field-events`;
    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      this.field_facilities.next(res.field_facilities);
      res.field_facilities.filter((item) => {
        if (item.id == id) {
          this.selectedFacility$.next(item);
        }
      });
    }, (err) => {
      this.isLoading.next(false);
    });
  }

  get_bus_contacts(id) {
    this.isLoading.next(true);
    let url = `field-events`;
    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      this.bus_contacts.next(res.bus_contacts);
      res.bus_contacts.filter((item) => {
        if (item.id == id) {
          this.selectedBuses$.next(item);
        }
      });
    }, (err) => {
      this.isLoading.next(false);
    });
  }


  get_ad_contacts(id) {
    this.isLoading.next(true);
    let url = `field-events`;
    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      this.ad_contacts.next(res.ad_contacts);
      res.ad_contacts.filter((item) => {
        if (item.id == id) {
          this.selectedAD$.next(item);
        }
      });
    }, (err) => {
      this.isLoading.next(false);
    });
  }

  add_ad_contact_locally(contact: any) {
    let current = this.ad_contacts.getValue();
    if (!current?.find(c => c.id === contact.id)) {
      this.ad_contacts.next([...current, contact]);
    }
  }

  get_referee_contacts(id) {
    this.isLoading.next(true);
    let url = `field-events`;
    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      this.referee_contacts.next(res.referee_contacts);
      res.referee_contacts.filter((item) => {
        if (item.id == id) {
          this.selectedReferee$.next(item);
        }
      });
    }, (err) => {
      this.isLoading.next(false);
    });
  }

  check_referee_availability(start_iso: string, end_iso: string, admin_id?: string, event_id?: string) {
    if (!start_iso || !end_iso) {
      this.blocked_referees.next([]);
      return;
    }

    let url = `field-events?type=get_all_referee_contacts&for_start=${encodeURIComponent(start_iso)}&for_end=${encodeURIComponent(end_iso)}`;
    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }
    
    if (event_id) {
      url += `&exclude_event_id=${event_id}`;
    }

    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      const blocked = (res?.contacts || [])
        .filter((c: any) => c.is_blocked)
        .map((c: any) => ({
          referee_contact_id: c.id,
          name: c.name,
          label: c.block_label,
        }));
      this.blocked_referees.next(blocked);
    }, () => {
      this.blocked_referees.next([]);
    });
  }

  get_event_staff_contacts(id) {
    this.isLoading.next(true);
    let url = `field-events`;
    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      this.event_staff_contacts.next(res.event_staff_contacts);
      res.event_staff_contacts.filter((item) => {
        if (item.id == id) {
          this.selectedEventStaff$.next(item);
        }
      });
    }, (err) => {
      this.isLoading.next(false);
    });
  }

  get_teaming_up_groups_by_search(search: string) {
    if (search) {
      this.isTeamsLoading.next(true);
      let url = `teamingUp?type=get_groups&search=${search}&page=1&limit=10&admin_only=true`;

      this.api.get(url).pipe(take(1)).subscribe((res: any) => {
        this.isTeamsLoading.next(false);
        this.teams.next(res.groups);
      }, (err) => {
        this.isTeamsLoading.next(false);
      })
    } else {
      this.teams.next([]);
    }
  }

  get_teaming_up_groups(search: string = '', email: string) {
    this.isTeamsLoading.next(true);
    let url = `teamingUp?search=${search}`;

    if (email) {
      url += `&type=get_teams_of_admin&email=${email}`;
    } else {
      url += `&type=get_groups&page=1&limit=12&admin_only=true`;
    }

    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isTeamsLoading.next(false);
      this.teaming_groups.next(res.teams || res.groups || []);
    }, (err) => {
      this.isTeamsLoading.next(false);
    })
  }

  get_opposing_teams(search: string = '', email: string) {
    this.isTeamsLoading.next(true);
    let url = `teamingUp?search=${search}`;

    if (email) {
      url += `&type=get_teams_of_admin&email=${email}`;
    } else {
      url += `&type=get_groups&page=1&limit=12&admin_only=true`;
    }

    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isTeamsLoading.next(false);
      this.opposing_teams.next(res.teams || res.groups || []);
    }, (err) => {
      this.isTeamsLoading.next(false);
    })
  }


  update_field_event(event: create_event, admin: any = null) {
    this.isLoading.next(true);
    let url = `field-events?type=update_field_event`;

    if (admin?.ad_admin_id) {
      url += `&admin_id=${admin.ad_admin_id}`;
    }

    if (admin?.ad_admin_email) {
      url += `&admin_email=${admin.ad_admin_email}`;
    }

    this.api.patch(url, event).pipe(take(1)).subscribe(async (res: any) => {
      this.isLoading.next(false);
      const t = await this.toast.create({
        header: 'Field Event Updated',
        message: `Field Event has been updated successfully.`,
        duration: 5000,
        color: 'success'
      });
      await t.present();
      this.event_updated.next("updated");
      this.toggle_add_menu(false);
      this.fieldBll.get_all_sessions(moment().format('YYYY-MM-DD'), admin);
      this.fieldBll.get_todays_sessions(admin);
      this.fieldScheduleReportBLL.get_sessions();
    }, async (err) => {
      if (err?.status === 409) {
        this.isLoading.next(false);
        this.toggle_add_menu(false);
        const t = await this.toast.create({
          header: 'Error',
          message: err.error.message,
          duration: 5000,
          color: 'danger'
        });
        await t.present();
        return;
      }
      this.isLoading.next(false);
      this.toggle_add_menu(false);
    });
  }

  create_new_field_event(event: create_event, admin) {
    this.isLoading.next(true);
    let url = `field-events?type=create-field-event&timezone=${moment.tz.guess()}`;

    if (admin?.ad_admin_id) {
      url += `&admin_id=${admin.ad_admin_id}`;
    }

    if (admin?.ad_admin_email) {
      url += `&admin_email=${admin.ad_admin_email}`;
    }

    this.api.post(url, event).pipe(take(1)).subscribe(async (res: any) => {
      this.isLoading.next(false);
      const t = await this.toast.create({
        header: 'Field Event',
        message: `New Field Event has been create successfully.`,
        duration: 5000,
        color: 'success'
      });
      await t.present();
      this.event_created.next(`${res.data.id},${res.data.event_id}`);
      this.toggle_add_menu(false);

      this.fieldBll.get_all_sessions(moment().format('YYYY-MM-DD'), admin);
      this.fieldBll.get_todays_sessions(admin);
      this.fieldScheduleReportBLL.get_sessions();

    }, async (err) => {
      if (err?.status === 409) {
        this.isLoading.next(false);
        this.toggle_add_menu(false);
        const t = await this.toast.create({
          header: 'Error',
          message: err.error.message,
          duration: 5000,
          color: 'danger'
        });
        await t.present();
        return;
      }

      this.isLoading.next(false);
      const t = await this.toast.create({
        header: 'Error',
        message: `There was an issue creating this event. Please try again.`,
        duration: 5000,
        color: 'danger'
      });
      await t.present();
      this.toggle_add_menu(false);
    });
  }

  create_practice_event(event: any, admin: any = null) {
    this.practiceLoading.next(true);
    let url = `field-events?type=create_practice_event&timezone=${moment.tz.guess()}`;

    if (admin?.ad_admin_id) {
      url += `&admin_id=${admin.ad_admin_id}`;
    }

    if (admin?.ad_admin_email) {
      url += `&admin_email=${admin.ad_admin_email}`;
    }

    this.api.post(url, event).pipe(take(1)).subscribe(async (res: any) => {
      this.practiceLoading.next(false);
      const t = await this.toast.create({
        header: 'Practice Event',
        message:`Practice event has been created successfully.`,
        duration: 5000,
        color: 'success'
      });
      await t.present();
      this.practice_created.next('created');
      this.toggle_add_menu(false);
      this.fieldBll.get_all_sessions(moment().format('YYYY-MM-DD'), admin);
      this.fieldBll.get_todays_sessions(admin);
      this.fieldScheduleReportBLL.get_sessions();
    }, async (err) => {
      if (err?.status === 409) {
        this.practiceLoading.next(false);
        this.toggle_add_menu(false);
        const t = await this.toast.create({
          header: 'Error',
          message: err.error.message,
          duration: 5000,
          color: 'danger'
        });
        await t.present();
        return;
      }

      this.practiceLoading.next(false);
      const t = await this.toast.create({
        header: 'Error',
        message: `There was an issue creating this practice event. Please try again.`,
        duration: 5000,
        color: 'danger'
      });
      await t.present();
      this.toggle_add_menu(false);
    });
  }

  update_practice_event(event: any, admin: any = null) {
    this.practiceLoading.next(true);
    let url = `field-events?type=update_practice_event&timezone=${moment.tz.guess()}`;

    if (admin?.ad_admin_id) {
      url += `&admin_id=${admin.ad_admin_id}`;
    }

    if (admin?.ad_admin_email) {
      url += `&admin_email=${admin.ad_admin_email}`;
    }

    this.api.patch(url, event).pipe(take(1)).subscribe(async (res: any) => {
      this.practiceLoading.next(false);
      const t = await this.toast.create({
        header: 'Practice Event Updated',
        message: `Practice event has been updated successfully.`,
        duration: 5000,
        color: 'success'
      });
      await t.present();
      this.practice_updated.next('updated');
      this.toggle_add_menu(false);
      this.fieldBll.get_all_sessions(moment().format('YYYY-MM-DD'), admin);
      this.fieldBll.get_todays_sessions(admin);
      this.fieldScheduleReportBLL.get_sessions();
    }, async (err) => {
      if (err?.status === 409) {
        this.practiceLoading.next(false);
        this.toggle_add_menu(false);
        const t = await this.toast.create({
          header: 'Error',
          message: err.error.message,
          duration: 5000,
          color: 'danger'
        });
        await t.present();
        return;
      }

      this.practiceLoading.next(false);
      const t = await this.toast.create({
        header: 'Error',
        message: `There was an issue updating this practice event. Please try again.`,
        duration: 5000,
        color: 'danger'
      });
      await t.present();
      this.toggle_add_menu(false);
    });
  }

  reset_practice_created() {
    this.practice_created.next('');
  }

  reset_practice_loading() {
    this.practiceLoading.next(false);
  }

  reset_practice_updated() {
    this.practice_updated.next('');
  }

  reset_event_created() {
    this.event_created.next('');
  }

  reset_event_update() {
    this.event_updated.next('');
  }

  reset_teams_observable() {
    this.teams.next([]);
  }

  reset_teaming_groups_observable() {
    this.teaming_groups.next([]);
  }

  reset_opposing_teams_observable() {
    this.opposing_teams.next([]);
  }

  attach_event_occurrence(event: any) {
    this.event.next(event);
  }

  reset_event_occurrence() {
    this.event.next(null);
  }

}